import { useState } from "react";
import { useQuery, useMutation, useIsMutating } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledActionItemButtons,
  StyledEditItemButton,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { MessageBox, CustomConfirmButton } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { getSearchParams } from "../../utils";

const BuyerAPISettingsList = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });

  const readData = async () => {
    try {
      const response = await API.get(
        `/buyer/api-settings?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  //   const updateData = async ({ value, newData }) => {
  //     try {
  //       newData.apiSettingStatus = value === true ? `Active` : `Inactive`;
  //       const response = await API.put(
  //         `/buyer/api-settings/${newData.id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}`,
  //         newData
  //       );
  //       return response.data;
  //     } catch (error) {
  //       throw new Error(error);
  //     }
  //   };

  const deleteData = async (id) => {
    try {
      const response = await API.delete(
        `/buyer/api-settings/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["buyer_api_settings", paginationData],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  //   const { mutateAsync: updateDataMutation } = useMutation({
  //     mutationFn: updateData,
  //     onError: (error) => console.error(error),
  //     onSuccess: () => {
  //       reloadData();
  //     },
  //   });

  const { mutateAsync: deleteDataMutation } = useMutation({
    mutationFn: deleteData,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const isMutation = useIsMutating();

  const renderPageHeading = () => (
    <PageHeader
      title="API Settings"
      showAddButton={true}
      addLink="create"
      addTitle="Create API Setting"
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isMutation > 0 || isRefetching} />
      {renderPageHeading()}
      <StyledContentContainer graybackground="true">
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "Name",
                  dataSelector: "name",
                  dataType: "string",
                },
                {
                  title: "IP Address",
                  dataSelector: "ipAddress",
                  dataType: "string",
                },
                {
                  title: "API Id",
                  dataSelector: "apiId",
                  dataType: "string",
                },
                {
                  title: "API Token",
                  dataSelector: "apiToken",
                  dataType: "string",
                },
                {
                  title: "Callback URL",
                  dataSelector: "callbackUrl",
                  dataType: "string",
                  cellrenderer: (value) => `
                 ${
                   value ? value : "https://www.yourdomain.com"
                 }/recharge_callback?status=[TRANSACTION_STATUS]&txn_id=[TRANSACTION_ID]&operators_id=[OPERATOR_ID]&customer_account_number=[CUSTOMER_ACCOUNT_NUMBER]&customer_mobile=[CUSTOMER_MOBILE_NUMBER]&amount=[AMOUNT]&payment_time=[PAYMENT_TIME]&ref_number=[YOUR_REF_NUMBER]`,
                },
                {
                  title: "",
                  dataSelector: "id",
                  canSort: false,
                  isSticky: "Yes",
                  stickyAlign: "right",
                  isDummy: "Yes",
                  textAlign: "center",
                  width: "max-content",
                  cellrenderer: (value, item) => (
                    <StyledActionItemButtons>
                      <StyledEditItemButton
                        to={`update/${value}`}
                        title="Update API Setting"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </StyledEditItemButton>
                      <CustomConfirmButton
                        showTitle={false}
                        onClick={async () => await deleteDataMutation(value)}
                      />
                    </StyledActionItemButtons>
                  ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(BuyerAPISettingsList);
